// Admin: configure a product's storefront 3D configurator. Lets the admin:
//  - enable it,
//  - add one or more BASE OPTIONS (each is an uploaded model file the customer
//    can switch between, e.g. "Wooden legs" / "Metal legs"),
//  - map each base model's parts to a material channel,
//  - give each part a custom customer-facing label,
//  - restrict which linked finishes/fabrics appear for each part.
// Writes { enabled, models:[{url,format,label,mappings}], labels, channelOptions }
// up via onChange; saved with the product.
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import {
  loadModelFromUrl, LoadedModel, buildColorMaterial, matKey,
  CHANNELS, FABRIC_CHANNEL_IDS, FINISH_CHANNEL_IDS,
  detectModelFormat, DEFAULT_CHANNEL_LABELS,
} from '../configurator/core';

// Distinct placeholder colors so the admin can SEE which part is on which
// channel in the preview.
const PREVIEW_MATS = {
  metal: buildColorMaterial('#9aa0a6', { metalness: 0.9, roughness: 0.3, name: 'pv-metal' }),
  wood: buildColorMaterial('#8a5a2b', { metalness: 0, roughness: 0.7, name: 'pv-wood' }),
  cane: buildColorMaterial('#d9c08a', { metalness: 0, roughness: 0.8, name: 'pv-cane' }),
  fabric1: buildColorMaterial('#4a78c2', { metalness: 0, roughness: 0.95, name: 'pv-f1' }),
  fabric2: buildColorMaterial('#3fae6a', { metalness: 0, roughness: 0.95, name: 'pv-f2' }),
  fabric3: buildColorMaterial('#c2503f', { metalness: 0, roughness: 0.95, name: 'pv-f3' }),
};

export default function ProductConfiguratorSetup({ models = [], fabrics = [], finishes = [], value, onChange }) {
  const enabled = !!value?.enabled;
  // EDITOR list — unlike the storefront normalizer, keep base rows that don't
  // have a model picked yet, so "Add base option" actually shows a new row to
  // choose a model in. Incomplete rows are skipped at render-time by the
  // storefront's own normalizer.
  const baseModels = useMemo(() => {
    if (Array.isArray(value?.models)) {
      return value.models.map((m) => ({
        url: m?.url || '',
        format: m?.format || (m?.url ? detectModelFormat(m.url) : ''),
        label: m?.label || '',
        mappings: m?.mappings || {},
      }));
    }
    if (value?.modelUrl) {
      return [{ url: value.modelUrl, format: value.format || detectModelFormat(value.modelUrl), label: '', mappings: value.mappings || {} }];
    }
    return [];
  }, [value]);
  const labels = useMemo(() => value?.labels || {}, [value]);
  const channelOptions = useMemo(() => value?.channelOptions || {}, [value]);

  // Which base is being previewed / edited.
  const [activeIdx, setActiveIdx] = useState(0);
  // Per-URL model discovery cache: { [url]: { state, scene, materials, error } }.
  const [disc, setDisc] = useState({});
  const reqRef = useRef(0);
  const seededRef = useRef(new Set());

  // Emit a new configurator object. Always carries the canonical fields so the
  // legacy single-model shape is migrated away on first edit.
  const emit = (next) => onChange({ enabled, models: baseModels, labels, channelOptions, ...next });

  const activeBase = baseModels[activeIdx] || null;
  const activeUrl = activeBase?.url || '';
  const activeDisc = activeUrl ? disc[activeUrl] : null;

  // Load + discover materials for the active base's model.
  useEffect(() => {
    if (!enabled || !activeUrl) return;
    if (disc[activeUrl] && disc[activeUrl].state !== 'error') return; // cached
    const format = activeBase?.format || detectModelFormat(activeUrl);
    const myReq = ++reqRef.current;
    setDisc((d) => ({ ...d, [activeUrl]: { state: 'loading' } }));
    loadModelFromUrl(activeUrl, format)
      .then(({ scene, materials }) => {
        if (myReq !== reqRef.current) return;
        setDisc((d) => ({ ...d, [activeUrl]: { state: 'ready', scene, materials } }));
      })
      .catch((err) => {
        if (myReq !== reqRef.current) return;
        const msg = err?.message?.includes('CORS')
          ? 'Could not load model (storage CORS). See setup notes.'
          : 'Could not load this model file.';
        setDisc((d) => ({ ...d, [activeUrl]: { state: 'error', error: msg } }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, activeUrl, activeBase?.format]);

  // Seed unmapped materials of the active base with their auto-classified channel
  // (once per base+url).
  useEffect(() => {
    if (!activeBase || !activeUrl || activeDisc?.state !== 'ready') return;
    const seedKey = `${activeIdx}:${activeUrl}`;
    if (seededRef.current.has(seedKey)) return;
    const seeded = { ...(activeBase.mappings || {}) };
    let changed = false;
    (activeDisc.materials || []).forEach((m) => {
      const k = matKey(m.name);
      if (seeded[k] === undefined) { seeded[k] = m.autoChannel; changed = true; }
    });
    seededRef.current.add(seedKey);
    if (changed) updateBase(activeIdx, { mappings: seeded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBase, activeUrl, activeDisc, activeIdx]);

  // ── Base option mutators ─────────────────────────────────────────────────
  const updateBase = (i, partial) => {
    const next = baseModels.map((b, idx) => (idx === i ? { ...b, ...partial } : b));
    emit({ models: next });
  };
  const addBase = () => {
    emit({ models: [...baseModels, { url: '', format: '', label: '', mappings: {} }] });
    setActiveIdx(baseModels.length);
  };
  const removeBase = (i) => {
    const next = baseModels.filter((_, idx) => idx !== i);
    emit({ models: next });
    setActiveIdx((cur) => Math.max(0, cur >= next.length ? next.length - 1 : cur));
  };
  const setBaseUrl = (i, url) => {
    seededRef.current.delete(`${i}:${baseModels[i]?.url}`);
    updateBase(i, { url, format: detectModelFormat(url), mappings: {} });
    setActiveIdx(i);
  };
  const setMapping = (i, materialName, channel) =>
    updateBase(i, { mappings: { ...(baseModels[i]?.mappings || {}), [matKey(materialName)]: channel } });

  // ── Channels actually used across ALL bases (drive labels + restrictions) ──
  const usedChannels = useMemo(() => {
    const set = new Set();
    baseModels.forEach((b) => Object.values(b.mappings || {}).forEach((c) => {
      if (c && c !== 'ignore') set.add(c);
    }));
    // Keep a stable, sensible order.
    return [...FINISH_CHANNEL_IDS, ...FABRIC_CHANNEL_IDS].filter((c) => set.has(c));
  }, [baseModels]);

  const setLabel = (channel, text) => emit({ labels: { ...labels, [channel]: text } });

  // Set a channel's full allow-list of option ids ([] = show all).
  const setOptionIds = (channel, key, ids) =>
    emit({ channelOptions: { ...channelOptions, [channel]: { ...channelOptions[channel], [key]: ids } } });

  // Linked fabrics/finishes sorted by their group/type so the Autocomplete's
  // grouped headers come out in order.
  const sortedFabrics = useMemo(
    () => [...fabrics].sort((a, b) => (a.typeName || '').localeCompare(b.typeName || '') || a.name.localeCompare(b.name)),
    [fabrics]
  );
  const sortedFinishes = useMemo(
    () => [...finishes].sort((a, b) => (a.groupName || '').localeCompare(b.groupName || '') || a.name.localeCompare(b.name)),
    [finishes]
  );

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>3D Configurator</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Let storefront customers customize this product on its 3D model. Add one or more base
          options (e.g. different legs), map each model's parts to a material channel, name the parts,
          and choose which linked fabrics &amp; finishes appear for each part.
        </Typography>

        <FormControlLabel
          control={<Checkbox checked={enabled} onChange={(e) => emit({ enabled: e.target.checked })} />}
          label="Enable 3D configurator on storefront"
        />

        {enabled && (
          <Box sx={{ mt: 1.5 }}>
            {models.length === 0 ? (
              <Alert severity="info">Upload a 3D model in the section above first, then add it as a base option here.</Alert>
            ) : (
              <>
                {/* ── Base options ── */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Base options</Typography>
                {baseModels.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No base options yet. Add one to begin.
                  </Typography>
                )}
                {baseModels.map((b, i) => (
                  <Box
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, borderRadius: 1.5,
                      border: '1px solid', borderColor: i === activeIdx ? 'primary.main' : 'divider',
                      cursor: 'pointer', bgcolor: i === activeIdx ? 'action.hover' : 'transparent',
                    }}
                  >
                    <TextField
                      select size="small" sx={{ minWidth: 200 }} label="Model"
                      value={b.url}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setBaseUrl(i, e.target.value)}
                    >
                      <MenuItem value=""><em>Select a model…</em></MenuItem>
                      {models.map((m) => (
                        <MenuItem key={m.url} value={m.url}>{m.filename || m.url.split('/').pop()}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small" sx={{ flex: 1 }} label="Label (shown to customer)"
                      placeholder="e.g. Wooden legs"
                      value={b.label || ''}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => updateBase(i, { label: e.target.value })}
                    />
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeBase(i); }} aria-label="Remove base option">
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<AddRounded />} onClick={addBase} sx={{ textTransform: 'none', mb: 2 }}>
                  Add base option
                </Button>

                {/* ── Active base: preview + part mapping ── */}
                {activeBase && activeUrl && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <Box sx={{ height: 320, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', position: 'relative', bgcolor: '#f4f6fb' }}>
                      {activeDisc?.state === 'loading' && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                          <CircularProgress size={26} />
                        </Box>
                      )}
                      {activeDisc?.state === 'error' && (
                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 2 }}>
                          <Typography variant="body2" color="error" align="center">{activeDisc.error}</Typography>
                        </Box>
                      )}
                      {activeDisc?.state === 'ready' && activeDisc.scene && (
                        <Canvas shadows flat camera={{ position: [3, 2.5, 4], fov: 42 }} gl={{ antialias: true }} style={{ background: '#f4f6fb' }}>
                          <Environment preset="studio" environmentIntensity={0.45} />
                          <ambientLight intensity={0.25} />
                          <directionalLight position={[5, 8, 5]} intensity={0.65} />
                          <LoadedModel scene={activeDisc.scene} mappings={activeBase.mappings || {}} channelMaterials={PREVIEW_MATS} />
                          <OrbitControls enablePan={false} />
                        </Canvas>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Map each part of <b>{activeBase.label || 'this base'}</b>. Preview colors: blue/green/red = Fabric 1/2/3, grey = metal, brown = wood, tan = cane.
                      </Typography>
                      {activeDisc?.state === 'ready' && (activeDisc.materials || []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">No materials found in this model.</Typography>
                      )}
                      {(activeDisc?.materials || []).map((m) => (
                        <Box key={m.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.name}>
                            {m.name}
                          </Typography>
                          <TextField
                            select size="small" sx={{ width: 150 }}
                            value={activeBase.mappings?.[matKey(m.name)] ?? m.autoChannel}
                            onChange={(e) => setMapping(activeIdx, m.name, e.target.value)}
                          >
                            {CHANNELS.map((c) => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                          </TextField>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* ── Parts: per-part label + which fabrics/finishes appear ── */}
                {usedChannels.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Parts</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Name each part and choose which of this product's linked fabrics/finishes the customer can pick for it.
                      Leave the picker empty to show every linked option.
                    </Typography>
                    {usedChannels.map((ch) => {
                      const isFabric = FABRIC_CHANNEL_IDS.includes(ch);
                      const opts = isFabric ? sortedFabrics : sortedFinishes;
                      const key = isFabric ? 'fabricPatternIds' : 'finishTypeIds';
                      const selectedIds = channelOptions[ch]?.[key] || [];
                      const selectedOpts = opts.filter((o) => selectedIds.includes(o.id));
                      return (
                        <Box key={ch} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                            {DEFAULT_CHANNEL_LABELS[ch] || ch}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({isFabric ? 'fabric' : 'finish'} part)
                            </Typography>
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px 1fr' }, gap: 1.5, alignItems: 'flex-start' }}>
                            <TextField
                              size="small"
                              label="Custom label"
                              placeholder={DEFAULT_CHANNEL_LABELS[ch] || ch}
                              value={labels[ch] || ''}
                              onChange={(e) => setLabel(ch, e.target.value)}
                            />
                            {opts.length === 0 ? (
                              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                No linked {isFabric ? 'fabrics' : 'finishes'} on this product yet.
                              </Typography>
                            ) : (
                              <Autocomplete
                                multiple
                                size="small"
                                options={opts}
                                value={selectedOpts}
                                groupBy={(o) => (isFabric ? o.typeName : o.groupName) || (isFabric ? 'Fabric' : 'Finishes')}
                                getOptionLabel={(o) => o.name}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                onChange={(_, val) => setOptionIds(ch, key, val.map((o) => o.id))}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={`${isFabric ? 'Fabrics' : 'Finishes'} for this part`}
                                    placeholder={selectedOpts.length ? 'Add more…' : 'All (type to limit)…'}
                                  />
                                )}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
